import * as React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ImagePlus, X } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';
import type { ReelItem } from '@/lib/reel';
import { setReelHandoff } from './handoff';
import { costLabel, freeLabel } from './labels';
import type { StudioCost } from './types';

// THE HINGE (blueprint section 03, 2026-10-16): "Make one like this" lifts
// this sheet with the output's recipe filled in (studio, format, length,
// style) and step 1 of its studio right here. Three heights: Peek, Half,
// Full. Drag the handle between them; drag below Peek, press Esc or Android
// Back (the Reel turns all three into onClose) and the Reel is back on the
// same video at the same second. Nothing is charged here.

export type SheetHeight = 'peek' | 'half' | 'full';
const HEIGHTS: Record<SheetHeight, number> = { peek: 38, half: 64, full: 92 }; // dvh
const ORDER: SheetHeight[] = ['peek', 'half', 'full'];

// The rights-consent tool each upload counts toward (components/consent):
// a yes in the sheet is the same yes in the studio.
const CONSENT: Partial<Record<ReelItem['studio'], string>> = { music: 'corrido', ads: 'ads' };

interface Props {
  item: ReelItem;
  /** Price line for this studio, already worded ("from 35 cr", "$2 per file"). */
  cost?: StudioCost;
  price?: string | null;
  onClose: () => void;
}

/** The studio entry with the recipe on it, so the studio preselects it. */
export function makeLink(item: ReelItem): string {
  const params = new URLSearchParams({ from: 'reel' });
  if (item.style) params.set('style', item.style);
  if (item.spot) params.set('length', String(item.spot));
  return `${item.makePath}?${params.toString()}`;
}

export function ReelSheet({ item, cost, price, onClose }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [height, setHeight] = React.useState<SheetHeight>('half');
  const [drag, setDrag] = React.useState<number | null>(null);
  const start = React.useRef<{ y: number; h: number } | null>(null);
  const [files, setFiles] = React.useState<File[]>([]);
  const [text, setText] = React.useState('');
  const fileInput = React.useRef<HTMLInputElement>(null);
  const studio = item.studio;
  const takesPhoto = studio === 'ads' || studio === 'music';
  const takesText = studio === 'story';
  const step = studio === 'quick' ? `quick.${item.key}` : studio;

  const vh = () => (typeof window !== 'undefined' ? window.innerHeight / 100 : 8);
  const onPointerDown = (e: React.PointerEvent) => {
    start.current = { y: e.clientY, h: HEIGHTS[height] };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!start.current) return;
    setDrag(Math.max(8, Math.min(96, start.current.h - (e.clientY - start.current.y) / vh())));
  };
  const onPointerUp = () => {
    if (!start.current) return;
    const at = drag ?? HEIGHTS[height];
    start.current = null;
    setDrag(null);
    if (at < HEIGHTS.peek - 12) return onClose();
    setHeight(ORDER.reduce((a, b) => (Math.abs(HEIGHTS[b] - at) < Math.abs(HEIGHTS[a] - at) ? b : a)));
  };
  const onHandleKey = (e: React.KeyboardEvent) => {
    const i = ORDER.indexOf(height);
    if (e.key === 'ArrowUp') { e.preventDefault(); setHeight(ORDER[Math.min(2, i + 1)]); }
    if (e.key === 'ArrowDown') { e.preventDefault(); if (i === 0) onClose(); else setHeight(ORDER[i - 1]); }
  };

  const go = () => {
    setReelHandoff({ studio, itemId: item.id, files: files.length ? files : undefined, text: text.trim() || undefined });
    trackEvent('reel_make_like_this', { item: item.id, studio, photos: files.length, text: Boolean(text.trim()) });
    navigate(makeLink(item));
  };

  const recipe: Array<[string, string]> = [
    [t('reel.sheet.studio'), t(`studios.${studio}.name`)],
    [t('reel.sheet.format'), t(`reel.recipes.${item.key}.format`)],
    [t('reel.sheet.length'), t(`reel.recipes.${item.key}.length`)],
    ...(item.style ? [[t('reel.sheet.style'), t(`corrido.styles.${item.style}`)] as [string, string]] : []),
  ];
  const free = freeLabel(cost, t);
  const paid = price ?? costLabel(cost, t);

  // Portaled: /watch is its own z-30 layer, and the phone tab bar (z-40) must not cover the sheet.
  return createPortal(
    <div className="fixed inset-0 z-[70]" data-testid="reel-sheet">
      <button type="button" aria-label={t('reel.sheet.backToVideo')} className="absolute inset-0 bg-black/40" onClick={onClose} tabIndex={-1} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="reel-sheet-title"
        data-height={height}
        className={`absolute inset-x-0 bottom-0 mx-auto flex max-w-xl flex-col rounded-t-[1.6rem] border border-white/10 bg-[hsl(219_22%_10%)] text-foreground shadow-[0_-24px_60px_-20px_rgba(0,0,0,0.9)] ${drag == null ? 'transition-[height] duration-300 ease-out motion-reduce:transition-none' : ''}`}
        style={{ height: `${drag ?? HEIGHTS[height]}dvh`, paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div
          role="slider"
          tabIndex={0}
          aria-label={t('reel.sheet.handle')}
          aria-valuemin={0}
          aria-valuemax={2}
          aria-valuenow={ORDER.indexOf(height)}
          aria-valuetext={t(`reel.sheet.height.${height}`)}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onKeyDown={onHandleKey}
          data-testid="reel-sheet-handle"
          className="flex cursor-grab touch-none flex-col items-center gap-1 pb-1 pt-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <span className="h-1.5 w-12 rounded-full bg-white/30" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{t('reel.sheet.dragHint')}</span>
        </div>

        <div className="flex items-start justify-between gap-3 px-5 pb-3">
          <div className="min-w-0">
            <h2 id="reel-sheet-title" className="font-display text-xl font-bold leading-tight">{t('reel.make')}</h2>
            <p className="mt-0.5 truncate text-sm text-muted-foreground">{t(`reel.items.${item.key}`)}</p>
          </div>
          <button type="button" onClick={onClose} className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.06] px-3 text-sm font-semibold hover:border-accent/50" data-testid="reel-sheet-close">
            <X className="h-4 w-4" aria-hidden /> {t('reel.sheet.backToVideo')}
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">{t('reel.sheet.recipe')}</p>
          <dl className="grid grid-cols-2 gap-2" data-testid="reel-sheet-recipe">
            {recipe.map(([k, v]) => (
              <div key={k} className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2">
                <dt className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{k}</dt>
                <dd className="text-sm font-semibold">{v}</dd>
              </div>
            ))}
          </dl>
          {(free || paid) && (
            <p className="mt-2 font-mono text-xs tabular-nums text-muted-foreground" data-testid="reel-sheet-price">
              {[free, paid && t('reel.sheet.thenPrice', { price: paid })].filter(Boolean).join(' · ')}
            </p>
          )}

          <div className="mt-4 rounded-2xl border border-accent/30 bg-accent/[0.06] p-4" data-testid="reel-sheet-step">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-accent">{t('reel.sheet.step1')}</p>
            <h3 className="mt-1 font-display text-lg font-bold">{t(`reel.sheet.first.${step}.title`)}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t(`reel.sheet.first.${step}.body`)}</p>

            {takesPhoto && (
              <div className="mt-3" data-consent-tool={CONSENT[studio]}>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/*"
                  multiple={studio === 'music'}
                  className="sr-only"
                  tabIndex={-1}
                  data-testid="reel-sheet-file"
                  onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, studio === 'music' ? 6 : 1))}
                />
                <button type="button" onClick={() => fileInput.current?.click()} className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-5 font-semibold hover:border-accent/60 hover:bg-accent/10" data-testid="reel-sheet-upload">
                  <ImagePlus className="h-4 w-4" aria-hidden />
                  {files.length ? t('reel.sheet.photosPicked', { count: files.length }) : t(`reel.sheet.first.${step}.button`)}
                </button>
              </div>
            )}
            {takesText && (
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                placeholder={t('reel.sheet.first.story.placeholder')}
                data-testid="reel-sheet-text"
                className="mt-3 w-full resize-y rounded-xl border border-white/15 bg-black/30 px-3 py-2 text-base leading-relaxed text-foreground placeholder:text-muted-foreground focus:border-accent/60 focus:outline-none"
              />
            )}

            <button
              type="button"
              onClick={go}
              data-testid="reel-sheet-continue"
              className="mt-4 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full bg-accent px-5 font-bold text-[#0b1416] transition hover:brightness-110"
            >
              {t('reel.sheet.continue', { studio: t(`studios.${studio}.name`) })}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </button>
            <p className="mt-2 text-center text-xs font-semibold text-accent">{t('reel.free')}</p>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default ReelSheet;
